import { Eye } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { ANCHOR_POINTS } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Drawer } from '../../../../components/Drawer';
import { StarRating } from '../../../../components/Stars';
import type { MatrixRow } from './MatrixTable';
import styles from './builder.module.css';

// "Preview scorecard" on the scoring matrix: what HR sees, drawn by the real screen's own piece — the
// star row of the sorting screen's "HR's ratings" (RV-2). ADR 0077 (C5): interviewers give one overall
// mark, so no interview round reads the matrix any more. Nothing in it can be filled in or saved.

interface ScorecardPreviewProps {
  /** The named lines, in matrix order. */
  readonly lines: readonly MatrixRow[];
  /** Said above the preview: what the matrix is for now. */
  readonly noRoundsNote: string;
}

export function ScorecardPreview({ lines, noRoundsNote }: ScorecardPreviewProps): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" icon={Eye} onClick={() => setIsOpen(true)}>
        Preview scorecard
      </Button>
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} title="Preview scorecard" meta="What HR sees. Nothing here is saved.">
        <div className={styles.preview}>
          <p className={styles.help}>{noRoundsNote}</p>
          <HrReviewPreview lines={lines.filter((line) => line.usedIn.includes('hr_review'))} />
        </div>
      </Drawer>
    </>
  );
}

function HrReviewPreview({ lines }: { readonly lines: readonly MatrixRow[] }): ReactNode {
  const titleId = useId();
  if (lines.length === 0) {
    return <p className={styles.help}>No qualities are ticked for HR review, so HR sees no star ratings. Tick &ldquo;HR review&rdquo; on a quality to add one.</p>;
  }
  return (
    <section className={styles.previewHr} aria-labelledby={titleId}>
      <p className={styles.help}>What HR sees beside each CV while reviewing.</p>
      <h3 id={titleId} className={styles.previewHrTitle}>
        HR&rsquo;s ratings
      </h3>
      <ul className={styles.previewHrList}>
        {lines.map((line) => (
          <li key={line.key} className={styles.previewHrLine}>
            <span className={styles.previewHrLabel}>{line.label}</span>
            <StarRating label={line.label} value={null} onChange={() => undefined} hints={anchorsOf(line)} isDisabled />
          </li>
        ))}
      </ul>
      <p className={styles.help}>Optional. They sit beside the ATS score and never change it.</p>
    </section>
  );
}

function anchorsOf(line: MatrixRow): Readonly<Record<string, string>> {
  return Object.fromEntries(
    ANCHOR_POINTS.flatMap((point): [string, string][] => {
      const text = line.anchors[point]?.trim();
      return text ? [[point, text]] : [];
    }),
  );
}
